import type { Lead, Membership, Priority, Stage, Task, TaskProgress } from "../../lib/types";
import { civilDate, daysBetween } from "../notifications/feed";
import { comparableText } from "../../ui/text";

/**
 * Modelo do quadro e da lista de Tarefas.
 *
 * Só funções puras: a página decide o que renderizar, mas a regra de qual
 * tarefa pertence a qual coluna, a qual visão e a qual funil mora aqui, para o
 * número do menu lateral e a lista exibida virem do mesmo cálculo (AGENTS §10).
 *
 * "Vencida" e "hoje" comparam o dia civil de `America/Sao_Paulo`, nunca o
 * prefixo do ISO em UTC (§7.6): um vencimento às 22h de Brasília ainda é hoje.
 */

/** Coluna do quadro: o andamento declarado, com "Concluídas" vindo do status. */
export const TASK_COLUMNS = [
  { id: "todo", label: "A fazer", tone: "var(--accent)" },
  { id: "doing", label: "Em andamento", tone: "var(--accent-2)" },
  { id: "waiting", label: "Aguardando", tone: "var(--warn)" },
  { id: "done", label: "Concluídas", tone: "var(--good)" },
] as const;

export type TaskColumnId = (typeof TASK_COLUMNS)[number]["id"];

/** Andamentos que o usuário declara; "Concluídas" é desfecho, não andamento. */
export const TASK_PROGRESS_COLUMNS = TASK_COLUMNS.filter(
  (column): column is (typeof TASK_COLUMNS)[number] & { id: TaskProgress } => column.id !== "done",
);

export const TASK_VIEWS = [
  { id: "todas", label: "Todas" },
  { id: "abertas", label: "Em aberto" },
  { id: "hoje", label: "Hoje" },
  { id: "semana", label: "Próximos 7 dias" },
  { id: "vencidas", label: "Vencidas" },
  { id: "concluidas", label: "Concluídas" },
  // Tarefa interna é a que não pertence a cadastro nenhum: preparar material,
  // revisar criativo, configurar rastreio (§7.5).
  { id: "internas", label: "Internas" },
] as const;

export type TaskViewId = (typeof TASK_VIEWS)[number]["id"];

export const DEFAULT_TASK_VIEW: TaskViewId = "todas";

export const TASK_PRIORITIES: Array<{ id: Priority; label: string; tone: string }> = [
  { id: "high", label: "Alta", tone: "var(--crit)" },
  { id: "medium", label: "Média", tone: "var(--accent)" },
  { id: "low", label: "Baixa", tone: "var(--muted)" },
];

/** Horizonte da visão "Próximos 7 dias", contado em dias civis inteiros. */
export const TASK_WEEK_DAYS = 7;

export function isTaskViewId(value: string | null): value is TaskViewId {
  return TASK_VIEWS.some((view) => view.id === value);
}

export function isTaskProgress(value: string | null): value is TaskProgress {
  return TASK_PROGRESS_COLUMNS.some((column) => column.id === value);
}

export function isPriority(value: string | null): value is Priority {
  return TASK_PRIORITIES.some((priority) => priority.id === value);
}

export function taskColumn(task: Task): TaskColumnId {
  return task.status === "done" ? "done" : task.progress;
}

export function priorityLabel(priority: Priority): string {
  return TASK_PRIORITIES.find((item) => item.id === priority)?.label ?? "Média";
}

export function priorityTone(priority: Priority): string {
  return TASK_PRIORITIES.find((item) => item.id === priority)?.tone ?? "var(--accent)";
}

/**
 * Tarefa vencida: pendente e com o instante de vencimento já passado.
 *
 * A comparação é por instante, não por dia, porque o prazo tem hora: às 15h
 * uma tarefa das 9h de hoje já está vencida.
 */
export function isTaskLate(task: Task, now: string): boolean {
  return task.status !== "done" && Date.parse(task.dueAt) < Date.parse(now);
}

/** Contexto de exibição de uma tarefa, resolvido uma vez por render. */
export interface TaskContext {
  lead?: Lead;
  membership?: Membership;
  stage?: Stage;
  funnelId?: string;
  /** Texto comparável para a busca: tarefa, observação, pessoa e empresa. */
  haystack: string;
}

export function buildTaskContexts(
  tasks: readonly Task[],
  leads: readonly Lead[],
  memberships: readonly Membership[],
  stages: readonly Stage[],
): Map<string, TaskContext> {
  const leadById = new Map(leads.map((lead) => [lead.id, lead]));
  const membershipById = new Map(memberships.map((membership) => [membership.id, membership]));
  const membershipByLead = new Map<string, Membership>();
  for (const membership of memberships) {
    if (!membershipByLead.has(membership.leadId)) membershipByLead.set(membership.leadId, membership);
  }
  const stageById = new Map(stages.map((stage) => [stage.id, stage]));

  const contexts = new Map<string, TaskContext>();
  for (const task of tasks) {
    const lead = task.leadId ? leadById.get(task.leadId) : undefined;
    // A tarefa aponta a participação quando nasceu dentro dela; senão vale a
    // única participação do lead (§7.2: um lead pertence a um funil só).
    const membership = task.membershipId
      ? membershipById.get(task.membershipId)
      : task.leadId
        ? membershipByLead.get(task.leadId)
        : undefined;
    const stage = membership ? stageById.get(membership.stageId) : undefined;
    contexts.set(task.id, {
      lead,
      membership,
      stage,
      funnelId: membership?.funnelId,
      haystack: comparableText(
        [task.description, task.note ?? "", lead?.name ?? "", lead?.company ?? ""].join(" "),
      ),
    });
  }
  return contexts;
}

export interface TaskViewInput {
  view: TaskViewId;
  /** Dia civil corrente (`YYYY-MM-DD`). */
  today: string;
  /** Instante corrente em ISO. */
  now: string;
  timeZone?: string;
}

export function matchesTaskView(task: Task, input: TaskViewInput): boolean {
  const due = civilDate(task.dueAt, input.timeZone);
  switch (input.view) {
    case "todas":
      return true;
    case "abertas":
      return task.status !== "done";
    case "hoje":
      return due === input.today;
    case "semana": {
      const distance = daysBetween(input.today, due);
      return distance >= 0 && distance <= TASK_WEEK_DAYS;
    }
    case "vencidas":
      return isTaskLate(task, input.now);
    case "concluidas":
      return task.status === "done";
    case "internas":
      return !task.leadId;
    default:
      return true;
  }
}

/** Ordem escolhida em "Filtros e ordenação". */
export const TASK_SORTS = [
  { id: "prazo", label: "Prazo mais próximo" },
  { id: "prioridade", label: "Maior prioridade" },
  { id: "titulo", label: "Título de A a Z" },
] as const;

export type TaskSortId = (typeof TASK_SORTS)[number]["id"];

export const DEFAULT_TASK_SORT: TaskSortId = "prazo";

export function isTaskSort(value: string | null): value is TaskSortId {
  return TASK_SORTS.some((sort) => sort.id === value);
}

/** Peso da prioridade na ordenação: alta primeiro. */
const PRIORITY_RANK: Record<Priority, number> = { high: 0, medium: 1, low: 2 };

/** Valor do filtro por cadastro que significa "só as tarefas internas". */
export const INTERNAL_TASK_OWNER = "internas";

export interface TaskFilterInput extends TaskViewInput {
  tasks: readonly Task[];
  contexts: Map<string, TaskContext>;
  /** `null` quando nenhum funil está selecionado. */
  funnelId: string | null;
  priority: Priority | null;
  query: string;
  /** Lead escolhido, `INTERNAL_TASK_OWNER` para internas, `null` para todos. */
  owner?: string | null;
  sort?: TaskSortId;
}

/**
 * Aplica visão, funil, prioridade e busca, e ordena.
 *
 * Pendentes vêm antes de concluídas; dentro de cada grupo, o prazo mais
 * próximo primeiro. Empate cai no texto para a ordem não oscilar entre
 * renders.
 */
export function filterTasks(input: TaskFilterInput): Task[] {
  const needle = comparableText(input.query);
  return input.tasks
    .filter((task) => {
      if (!matchesTaskView(task, input)) return false;
      const context = input.contexts.get(task.id);
      if (input.funnelId && context?.funnelId !== input.funnelId) return false;
      if (input.priority && task.priority !== input.priority) return false;
      if (input.owner === INTERNAL_TASK_OWNER && task.leadId) return false;
      if (input.owner && input.owner !== INTERNAL_TASK_OWNER && task.leadId !== input.owner)
        return false;
      if (needle && !(context?.haystack ?? "").includes(needle)) return false;
      return true;
    })
    .sort((a, b) => {
      // Concluída sempre depois, em qualquer ordem escolhida: o quadro é de
      // trabalho pendente, e o desfecho é o fim da fila.
      const byStatus = Number(a.status === "done") - Number(b.status === "done");
      if (byStatus !== 0) return byStatus;
      const byDue = Date.parse(a.dueAt) - Date.parse(b.dueAt);
      const byTitle = a.description.localeCompare(b.description, "pt-BR");
      if (input.sort === "prioridade") {
        const byPriority = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
        if (byPriority !== 0) return byPriority;
        return byDue !== 0 ? byDue : byTitle;
      }
      if (input.sort === "titulo") return byTitle !== 0 ? byTitle : byDue;
      return byDue !== 0 ? byDue : byTitle;
    });
}

export function groupTasksByColumn(tasks: readonly Task[]): Record<TaskColumnId, Task[]> {
  const groups: Record<TaskColumnId, Task[]> = { todo: [], doing: [], waiting: [], done: [] };
  for (const task of tasks) groups[taskColumn(task)].push(task);
  return groups;
}

export interface DueDisplay {
  /** Rótulo humano do dia: Hoje, Amanhã, Ontem ou a data. */
  label: string;
  time: string;
  late: boolean;
  today: boolean;
  /** Título completo, para quem precisa da data exata. */
  title: string;
}

const DATE_FORMAT = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
});

const TIME_FORMAT = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  hour: "2-digit",
  minute: "2-digit",
});

const FULL_FORMAT = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  dateStyle: "full",
  timeStyle: "short",
});

export function describeDue(task: Task, input: TaskViewInput): DueDisplay {
  const moment = new Date(task.dueAt);
  const due = civilDate(task.dueAt, input.timeZone);
  const distance = daysBetween(input.today, due);
  const label =
    distance === 0 ? "Hoje" : distance === 1 ? "Amanhã" : distance === -1 ? "Ontem" : DATE_FORMAT.format(moment);
  return {
    label,
    time: TIME_FORMAT.format(moment),
    late: isTaskLate(task, input.now),
    today: distance === 0 && task.status !== "done",
    title: FULL_FORMAT.format(moment),
  };
}
